import { QuickJSContext, type QuickJSHandle } from 'quickjs-emscripten'
import { ref, toRaw } from 'vue'
import useQuickJsContext from '@/composables/quickjs/wasm-context'

export default function useQuickJs() {
    const getWasmContext = useQuickJsContext()
    const vms = ref<QuickJSContext>()
    const isReady = ref(false)

    const load = async () => {
        vms.value = await getWasmContext()
        if (vms.value) {
            isReady.value = true
        }
    }

    const marshalData = (target: unknown) => {
        if (!vms.value) {
            return
        }

        const vm = toRaw(vms.value) as QuickJSContext

        switch (typeof target) {
            case 'number': {
                return vm.newNumber(target)
            }
            case 'string': {
                return vm.newString(target)
            }
            case 'undefined': {
                return vm.undefined
            }
            case 'boolean': {
                return target ? vm.true : vm.false
            }
            case 'object': {
                if (target === null) {
                    return vm.null
                }

                if (Array.isArray(target)) {
                    const array = vm.unwrapResult(vm.evalCode('([])'))
                    target.forEach(item => {
                        const marshaledItem = marshalData(item) as QuickJSHandle
                        const push = vm.getProp(array, 'push')
                        vm.callFunction(push, array, marshaledItem)
                    })

                    return array
                }

                const obj = vm.newObject()

                Object.keys(target).forEach(key => {
                    const value = target[key as keyof typeof target]
                    const marshaledKey = marshalData(key) as QuickJSHandle
                    const marshaledValue = marshalData(value) as QuickJSHandle

                    vm.setProp(obj, marshaledKey, marshaledValue)
                })

                return obj
            }
            default: {
                throw new Error(`${typeof target} is not supported`)
            }
        }
    }

    const runEvalCode = (vm: QuickJSContext, list: Record<string, string>, resultObj: QuickJSHandle) => {
        for (const key in list) {
            if (key in list) {
                const condition = list[key]
                const evalCondition = vm.evalCode(condition)

                if (evalCondition.error) {
                    throw evalCondition.error.consume(vm.dump)
                }

                const evalResult = evalCondition.value.consume<QuickJSHandle>(vm.dump)
                vm.setProp(resultObj, key, marshalData(evalResult) as QuickJSHandle)
            }
        }
    }

    const getPropValue = (vm: QuickJSContext, key: string) => vm.getProp(vm.global, key).consume(vm.dump)

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const setDataInContext = (vm: QuickJSContext, values: Record<string, any>) => {
        for (const key of Object.keys(values)) {
            const value = values[key]
            const marshaledData = marshalData(toRaw(value))
            if (marshaledData) {
                vm.setProp(vm.global, key, marshaledData)
            }
        }
    }

    return {
        vms,
        isReady,
        marshalData,
        runEvalCode,
        getPropValue,
        load,
        setDataInContext,
    }
}
